import { isPaidSubscription } from '@whispers/sdk';
import { useParams, Link } from 'react-router-dom';

import { useCards, useNote, useReading, useSpreads } from '../api.js';
import { Button } from '../components/Button.js';
import { Card } from '../components/Card.js';
import { CardSkeleton, Skeleton } from '../components/Skeleton.js';
import { useAuth } from '../contexts/AuthContext.js';
import { getCardUrl } from '../hooks/getCardUrl.js';
import { useShare } from '../hooks/useShare.js';
import { indexById } from '../utils/collections.js';

export const ReadingPage = () => {
  const { id } = useParams<{ id: string }>();
  const { canShare, shareLink } = useShare();
  const { user } = useAuth();

  const {
    data: readingData,
    isLoading: readingLoading,
    error,
  } = useReading({ readingId: id ?? '', include: ['draws'] });
  const { data: noteData } = useNote({
    readingId: id ?? '',
    enabled: user ? user.id === readingData?.reading.userId : false,
  });
  const { data: cardsData } = useCards({ include: ['decks'] });
  const { data: spreadsData } = useSpreads({ include: ['positions'] });

  if (readingLoading) {
    return (
      <>
        <div className="flex gap-3 overflow-x-auto py-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="aspect-[10/14] h-[100px] shrink-0 rounded-lg" />
          ))}
        </div>
        <CardSkeleton lines={3} />
      </>
    );
  }

  if (error || !readingData) {
    return (
      <Card>
        <div className="text-center">
          <h2 className="text-lg font-medium text-text">Reading not found</h2>
          <p className="mt-2 text-sm text-text-secondary">
            This reading may have been deleted or doesn't exist.
          </p>
          <Link to="/" className="mt-4 inline-block text-sm text-text underline">
            Go back home
          </Link>
        </div>
      </Card>
    );
  }

  const reading = readingData.reading;
  const draws = readingData.draws ?? [];
  const note = noteData?.note;

  const cardMap = indexById(cardsData?.cards ?? []);
  const deckSlug = indexById(cardsData?.decks ?? []).get(reading.deckId)?.slug;
  const positionMap = indexById(spreadsData?.positions ?? []);
  const spread = (spreadsData?.spreads ?? []).find((s) => s.id === reading.spreadId);

  return (
    <>
      <div>
        <h1 className="text-xl font-medium text-text">{spread?.name ?? 'Reading'}</h1>
        <p className="mt-1 text-sm text-text-secondary">
          {new Date(reading.createdAt).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          })}
        </p>
      </div>

      {/* Cards drawn */}
      <div className="grid gap-4">
        {draws.map((draw) => {
          const card = cardMap.get(draw.cardId);
          const position = positionMap.get(draw.positionId);
          return (
            <Card key={draw.id}>
              <div className="flex gap-4">
                {card && (
                  <img
                    className="h-28 shrink-0 rounded-md bg-surface-sunken"
                    src={getCardUrl({ slug: card.slug, deckSlug })}
                    alt={card?.name}
                  />
                )}
                <div className="flex-1">
                  <p className="text-xs font-medium uppercase tracking-wide text-disabled-text">
                    {position?.name}
                  </p>
                  <h3 className="mt-1 text-lg font-medium text-text">{card?.name}</h3>
                  {card?.description && (
                    <p className="mt-2 text-sm text-text-secondary">{card.description}</p>
                  )}
                </div>
                <div className="w-20 flex justify-end items-end">
                  <Button
                    href={`/decks/${deckSlug}/cards/${card?.slug}`}
                    rounded
                    size="sm"
                    aria-label="View card"
                  >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                      />
                    </svg>
                  </Button>
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Notes */}
      {user && note && (
        <Card>
          <h2 className="mb-2 text-sm font-medium uppercase tracking-wide text-disabled-text">
            Notes
          </h2>
          <p className="whitespace-pre-wrap text-text-secondary">{note.content}</p>
        </Card>
      )}

      {/* Actions */}
      <div className="flex flex-wrap justify-center gap-3">
        {user?.id === reading.userId && isPaidSubscription(user) && (
          <Link
            to={`/readings/${reading.id}/notes`}
            className="rounded-lg bg-button-solid-bg px-4 py-2 text-sm font-medium text-text-inverse transition-colors hover:bg-button-solid-bg-hover"
          >
            {note ? 'Edit Notes' : 'Add Notes'}
          </Link>
        )}

        {canShare && (
          <>
            {/* Share with card images */}
            {/* <button
              onClick={() =>
                void shareWithImages({
                  url: `${window.location.origin}/readings/${reading.id}`,
                  title: `My ${spread?.name ?? 'oracle'} reading`,
                  imageUrls: draws
                    .map((draw) => {
                      const card = cardMap.get(draw.cardId);
                      return card ? `/cards/jpg/${card.slug}.jpg` : null;
                    })
                    .filter((url): url is string => url !== null),
                })
              }
              className="rounded-lg border border-button-outline-border px-4 py-2 text-sm font-medium text-button-outline-border transition-colors hover:bg-button-outline-hover-bg"
            >
              Share reading
            </button> */}

            {/* Share link only */}
            <button
              onClick={() =>
                void shareLink(
                  `${window.location.origin}/readings/${reading.id}`,
                  `My ${spread?.name ?? 'oracle'} reading`,
                )
              }
              className="rounded-lg border border-button-outline-border px-4 py-2 text-sm font-medium text-button-outline-border transition-colors hover:bg-button-outline-hover-bg"
            >
              Copy link
            </button>
          </>
        )}
      </div>
    </>
  );
};
